import { useState, useEffect, useMemo } from 'react';
import { Edit, Eye, Filter, Building2, MapPin, Activity, RefreshCw, AlertTriangle, Users } from 'lucide-react';
import { api } from '@/utils/api';
import { toast } from 'sonner';

export default function AdminPropertiesTab({ onAddNew, onViewProject }) {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter states
  const [typeFilter, setTypeFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/projects/all');
      setProperties(response.data);
    } catch (error) {
      setProperties([]);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const types = [...new Set(properties.map(p => p.type).filter(Boolean))];
    const cities = [...new Set(properties.map(p => p.location?.city).filter(Boolean))];
    const states = [...new Set(properties.map(p => p.lifecycleState || 'DRAFT').filter(Boolean))];
    return { types, cities, states };
  }, [properties]);

  // Apply filters
  const filteredProperties = useMemo(() => {
    return properties.filter(property => {
      const matchesType = typeFilter === 'all' || property.type === typeFilter;
      const matchesCity = cityFilter === 'all' || property.location?.city === cityFilter;
      const matchesState = stateFilter === 'all' || (property.lifecycleState || 'DRAFT') === stateFilter;
      return matchesType && matchesCity && matchesState;
    });
  }, [properties, typeFilter, cityFilter, stateFilter]);

  const formatINR = (val) => {
    if (!val || val === 0) return '₹0';
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(2)} L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const getStateColor = (state) => {
    const colors = {
      DRAFT: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
      OPEN: 'bg-[#4ADE80]/10 text-[#4ADE80] border-[#4ADE80]/30',
      FUNDED: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      CLOSED: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      PAUSED: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
      EXITED: 'bg-white/10 text-white/60 border-white/30'
    };
    return colors[state] || colors.DRAFT;
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setCityFilter('all');
    setStateFilter('all');
  };

  const hasActiveFilters = typeFilter !== 'all' || cityFilter !== 'all' || stateFilter !== 'all';

  return (
    <div className="p-10">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-light text-white mb-2">All Properties</h1>
          <p className="text-sm text-gray-500">Manage property listings and funding</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchProperties} className="p-2 border border-white/10 text-gray-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onAddNew} className="bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold px-6 py-3 text-sm uppercase tracking-wider">
            Add New Property
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-6 p-4 bg-white/5 border border-white/10 rounded-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-white/50" />
          <span className="text-sm text-white/50 uppercase tracking-wider">Filters</span>
          {hasActiveFilters && (
            <button 
              onClick={clearFilters}
              className="ml-auto text-xs text-[#4ADE80] hover:text-[#4ADE80]/80"
            >
              Clear All
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Property Type Filter */}
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider block mb-2 flex items-center gap-1">
              <Building2 className="w-3 h-3" />
              Property Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-2 bg-[#030303] border border-white/10 rounded-sm text-white text-sm focus:outline-none focus:border-[#4ADE80]/50"
            >
              <option value="all">All Types</option>
              {filterOptions.types.map(type => (
                <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
              ))}
            </select>
          </div>

          {/* City Filter */}
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider block mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              Location
            </label>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="w-full px-4 py-2 bg-[#030303] border border-white/10 rounded-sm text-white text-sm focus:outline-none focus:border-[#4ADE80]/50"
            >
              <option value="all">All Cities</option>
              {filterOptions.cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Lifecycle State Filter */}
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider block mb-2 flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Status
            </label>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              className="w-full px-4 py-2 bg-[#030303] border border-white/10 rounded-sm text-white text-sm focus:outline-none focus:border-[#4ADE80]/50"
            >
              <option value="all">All States</option>
              <option value="DRAFT">Draft</option>
              <option value="OPEN">Open</option>
              <option value="FUNDED">Funded</option>
              <option value="CLOSED">Closed</option>
              <option value="PAUSED">Paused</option>
              <option value="EXITED">Exited</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-white/50">
        Showing {filteredProperties.length} of {properties.length} properties
      </div>

      {loading ? (
        <p className="text-gray-500">Loading...</p>
      ) : (
        <div className="border border-white/10">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 bg-white/5">
            <div className="col-span-3"><p className="text-xs text-gray-600 uppercase">Property</p></div>
            <div className="col-span-1"><p className="text-xs text-gray-600 uppercase">Type</p></div>
            <div className="col-span-1"><p className="text-xs text-gray-600 uppercase">Units</p></div>
            <div className="col-span-1"><p className="text-xs text-gray-600 uppercase">Status</p></div>
            <div className="col-span-1"><p className="text-xs text-gray-600 uppercase">Investors</p></div>
            <div className="col-span-2 text-right"><p className="text-xs text-gray-600 uppercase">Raised / Target</p></div>
            <div className="col-span-1"><p className="text-xs text-gray-600 uppercase">Pending</p></div>
            <div className="col-span-2 text-center"><p className="text-xs text-gray-600 uppercase">Actions</p></div>
          </div>

          {filteredProperties.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-gray-500">No properties match your filters</p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-sm text-[#4ADE80] mt-2">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            filteredProperties.map((property) => {
              const lifecycleState = property.lifecycleState || property.status || 'DRAFT';
              const unitCount = property.units?.length || property.unitCount || 0;
              const investorCount = property.investorCount || 0;
              const pendingActions = property.pendingActions || 0;
              return (
                <div key={property.propertyId || property.projectId} className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 hover:bg-white/5">
                  <div className="col-span-3">
                    <p className="text-sm text-white font-medium">{property.name}</p>
                    <p className="text-xs text-gray-600">{property.location?.city}, {property.location?.state}</p>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs px-2 py-1 bg-white/5 border border-white/10 rounded-sm text-gray-400 capitalize">
                      {property.type}
                    </span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-sm font-mono text-white">{unitCount}</span>
                    <span className="text-xs text-gray-500 ml-1">unit{unitCount !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="col-span-1">
                    <span className={`text-xs px-2 py-1 border rounded-sm ${getStateColor(lifecycleState)}`}>
                      {lifecycleState}
                    </span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-sm text-gray-400 flex items-center gap-1">
                      <Users className="w-3 h-3" /> {investorCount}
                    </span>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="font-mono text-sm text-[#4ADE80]">{formatINR(property.financial?.capitalRaised)}</p>
                    <p className="text-xs text-gray-500">of {formatINR(property.financial?.capitalTarget)} ({property.financial?.fundingPercentage?.toFixed(0) || 0}%)</p>
                  </div>
                  <div className="col-span-1">
                    {pendingActions > 0 ? (
                      <span className="text-xs text-amber-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {pendingActions}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">--</span>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-center gap-2">
                    <button 
                      onClick={() => onViewProject && onViewProject(property.projectId)}
                      className="px-3 py-1 text-xs border border-white/10 text-gray-400 hover:text-white hover:border-white/20 flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" />
                      View
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
